import { useEffect, useState } from "react";
import { guildApi, type PalaceDetailView } from "../../api/guild";
import { PlatinumSeatGrid } from "./PlatinumSeatGrid";
import { ModalBackdrop } from "../ModalBackdrop";

interface PalaceDetailModalProps {
  palace: string;
  onClose: () => void;
  onClaimed?: () => void;
}

export function PalaceDetailModal({ palace, onClose, onClaimed }: PalaceDetailModalProps) {
  const [detail, setDetail] = useState<PalaceDetailView | null>(null);
  const [loading, setLoading] = useState(true);
  const [claimLoading, setClaimLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const d = await guildApi.palaceDetail(palace);
        setDetail(d);
      } catch (e) {
        setMessage(e instanceof Error ? e.message : "Failed to load palace");
      } finally {
        setLoading(false);
      }
    })();
  }, [palace]);

  const claimDaily = async () => {
    setClaimLoading(true);
    setMessage(null);
    try {
      const res = await guildApi.claimPalaceDaily(palace);
      setMessage(res.message);
      const d = await guildApi.palaceDetail(palace);
      setDetail(d);
      onClaimed?.();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Claim failed");
    } finally {
      setClaimLoading(false);
    }
  };

  return (
    <ModalBackdrop onClose={onClose} className="z-40 items-end sm:items-center bg-black/70">
      <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl w-full max-w-md max-h-[85vh] overflow-y-auto p-4 space-y-3">
        <div className="flex justify-between items-start">
          <div>
            {detail && (
              <>
                <img src={detail.icon} alt="" className="w-10 h-10 mx-auto mb-1" />
                <h3 className="font-bold text-white text-center">{detail.name}</h3>
              </>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white text-lg leading-none">
            ✕
          </button>
        </div>

        {loading && <p className="text-xs text-gray-500 animate-pulse">Loading…</p>}

        {detail && !loading && (
          <>
            <p className="text-sm text-center text-gray-300">
              Holder:{" "}
              <span className="text-gold-300 font-semibold">{detail.holder_guild_name ?? "Unclaimed"}</span>
            </p>

            {(detail.daily_gold > 0 || detail.daily_contribution > 0) && (
              <div className="bg-kingdom-dark rounded-lg p-3 text-xs space-y-2">
                <p className="text-gray-400">Daily reward (guild members):</p>
                <p className="text-white">
                  {detail.daily_gold.toLocaleString()} gold + {detail.daily_contribution} contribution
                </p>
                {detail.daily_claim_available && (
                  <button
                    type="button"
                    disabled={claimLoading}
                    onClick={() => void claimDaily()}
                    className="w-full py-2 rounded-lg bg-emerald-700 text-white font-bold text-sm disabled:opacity-50"
                  >
                    Collect daily reward
                  </button>
                )}
                {detail.daily_claimed_today && !detail.daily_claim_available && (
                  <p className="text-emerald-400">Already collected today</p>
                )}
              </div>
            )}

            {detail.palace === "platinum" && detail.platinum_seats.length > 0 && (
              <div>
                <p className="text-xs text-violet-200 font-semibold mb-2">Platinum hierarchy</p>
                <PlatinumSeatGrid seats={detail.platinum_seats} showHolders />
              </div>
            )}

            {detail.conquerors.length > 0 && (
              <div>
                <p className="text-xs text-gray-400 font-semibold mb-1">Conquest thanks</p>
                <ul className="space-y-1 max-h-32 overflow-y-auto">
                  {detail.conquerors.map((c) => (
                    <li key={`${c.user_id}-${c.week_key}`} className="text-[10px] text-gray-300">
                      {c.display_name}
                      {c.guild_name && <span className="text-gray-500"> · {c.guild_name}</span>}
                      <span className="text-gray-600"> · {c.week_key}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {message && <p className="text-xs text-emerald-300">{message}</p>}
      </div>
    </ModalBackdrop>
  );
}
